import './style.scss';

import { FormControlLabel, Radio, RadioGroup } from '@mui/material';
import * as Sentry from '@sentry/browser';
import axios from 'axios';
import { useState } from 'react';
import DataTable from 'react-data-table-component';
import { toast as Toast } from 'react-toastify';
import { Alert, Button, Collapse, Modal, Spinner, Tooltip } from 'reactstrap';

import Dropzone from '@/components/dropzone';
import { Constants } from '@/constants';

const sendToDevs = (url: string) => {
  // TODO Email devs with the URL that does not scrape properly - for now, just log error to Sentry
  Sentry.captureException(`Unable to parse url: ${url}`);
};

const CigarImporter = ({
  open,
  onClose,
  importVitolasOnly = false,
  onRecievedCigar = () => {},
  onReceivedVitolas = () => {},
}) => {
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [selectedRecordCount, setSelectedRecordCount] = useState(0);
  const [showImporterDatatable, setShowImporterDatatable] = useState(false);
  const [cigarImportType, setCigarImportType] = useState<
    'single_import' | 'bulk_import'
  >('single_import');
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);
  const [showUrlTooltip, setShowUrlTooltip] = useState(false);
  const [selectedCigarsFile, setSelectedCigarsFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [unableToParse, setUnableToParse] = useState(false);

  const onSave = () => {
    console.log(`Getting cigar details for ${selectedUrl}`);
    setImporting(true);
    setUnableToParse(false);
    axios
      .get(`${Constants.apiPath}/scrape/cigar`, {
        params: {
          url: selectedUrl,
        },
      })
      .then((res) => {
        console.debug(JSON.stringify(res.data));
        setImporting(false);
        if (res.data.vitolas && importVitolasOnly) {
          console.debug('Got vitolas!');
          console.debug(res.data.vitolas);
          onReceivedVitolas(res.data.vitolas);
        }
        if (!importVitolasOnly) {
          onRecievedCigar(res.data);
        }
        if (typeof onClose === 'function') {
          onClose();
        }
      })
      .catch((e) => {
        setImporting(false);
        setUnableToParse(true);
        console.log(e.message);
      });
  };

  const onImport = () => {
    setUploading(true);
    // FIXME This needs to be an endpoint the business panel can access, as well... have an endpoint on the business
    //  panel just use a JWT to access the admin endpoint
    axios
      .post(`${Constants.apiPath}/cigars/datasources/sign`, {
        filetype: selectedCigarsFile.type,
      })
      .then((result) => {
        console.debug('Signed response:');
        console.debug(JSON.stringify(result.data.signed_url));
        const uploadOptions = {
          method: 'PUT',
          body: selectedCigarsFile,
        };

        console.debug(result.data);

        console.debug('Direct upload in progress...');
        fetch(result.data.signed_url, uploadOptions)
          .then(async () => {
            console.debug('S3 success');
            if (result.data.datasource_url) {
              // FIXME This needs to be an endpoint the business panel can access, as well...
              const batchEndpoint = `${Constants.apiPath}/cigars/batch`;
              console.debug('Kicking off step function...');
              await axios
                .post(batchEndpoint, {
                  datasource_url: result.data.datasource_url,
                })
                .then(() => {
                  // TODO Update local state with the new datasource? Or just show alert that it's importing and they will be notified when complete?
                  setUploading(false);
                  if (typeof onClose === 'function') {
                    onClose();
                  }
                  Toast.success('You will receive an email when complete', {
                    heading: 'Cigar Sync Started',
                    position: 'top-right',
                  });
                })
                .catch((err) => {
                  console.error(err);
                  setUploading(false);
                  // TODO Alert the user with ModalDialog? Reuse it for all of these catches...
                  alert(
                    'Something went wrong. Please contact our support team at support@boxpressd.com'
                  );
                });
            } else {
              console.error('No data URL...');
              setUploading(false);
              // Alert admins - something went wrong
              Sentry.setExtra('s3_signed_url_response', {
                id: result.id,
                datasource_url: result.datasource_url,
              });
              Sentry.captureException('Unable to upload media to S3');
              alert(
                'Something went wrong. Please contact our support team at support@boxpressd.com'
              );
            }
          })
          .catch((err) => {
            setUploading(false);
            console.debug('S3 failure:');
            console.debug(err);
            alert(
              'Something went wrong. Please contact our support team at support@boxpressd.com'
            );
          });
      })
      .catch((err) => {
        setUploading(false);
        console.error(err);
        alert(
          'Something went wrong. Please contact our support team at support@boxpressd.com'
        );
      });
  };

  const renderImportModal = () => (
    <Modal isOpen={open} onClosed={onClose} toggle={onClose}>
      <div className="modal-header">
        <h5 className="modal-title">
          {importVitolasOnly ? 'Import Vitolas' : 'Import Cigars'}
        </h5>
      </div>
      <div className="modal-body">
        <RadioGroup
          row
          aria-label="product import type"
          name="product_import_type"
          value={cigarImportType}
          onChange={(e) => setCigarImportType(e.target.value)}
        >
          <FormControlLabel
            value="single_import"
            control={<Radio color="primary" />}
            label={
              importVitolasOnly ? 'Vitolas from URL' : 'Single cigar from URL'
            }
            labelPlacement="end"
          />
          <FormControlLabel
            value="bulk_import"
            control={<Radio color="primary" />}
            label="Bulk import from file"
            labelPlacement="end"
          />
        </RadioGroup>
        <Collapse isOpen={cigarImportType === 'single_import'}>
          {'URL'} <i className="icon-info-alt" id="urlTooltip" />
          <Tooltip
            placement="top"
            isOpen={showUrlTooltip}
            target="urlTooltip"
            toggle={() => setShowUrlTooltip(!showUrlTooltip)}
            style={{ width: 300 }}
          >
            Importable URLs include: <br />
            <br />
            <ul>
              <li>https://www.cigarsinternational.com/p/[*]</li>
              <li>
                {'https://www.jrcigars.com/item/[*]'}
                <strong style={{ color: 'red' }}>
                  (currently unavailable)
                </strong>
              </li>
              <li>https://www.famous-smoke.com/[*]</li>
              <li>https://www.neptunecigar.com/cigar/[*]</li>
              <li>https://halfwheel.com/[*]</li>
            </ul>
          </Tooltip>
          <input
            className="form-control"
            type="text"
            name="import-url"
            value={selectedUrl}
            onChange={(e) => setSelectedUrl(e.target.value)}
          />
          {unableToParse && (
            <Alert color="danger" style={{ marginTop: 10 }}>
              <span>Unable to parse URL. Please try a different one.</span>
              <Button
                color="danger"
                onClick={() => {
                  sendToDevs(selectedUrl);
                  setUnableToParse(false);
                  setSelectedUrl('');
                  Toast.success(
                    'URL sent to developers, please try a different one.',
                    {
                      position: 'top-right',
                    }
                  );
                }}
              >
                Request Fix
              </Button>
            </Alert>
          )}
        </Collapse>
        <Collapse isOpen={cigarImportType === 'bulk_import'}>
          <div>File Upload</div>
          <Dropzone
            id="dropzone"
            onChange={(files) => {
              // eslint-disable-next-line no-console
              console.log(files);
              // setState(
              //   {
              //     selectedInventoryFile: files[0],
              //   },
              //   () => {
              //     onCloseModal('import');
              //     onOpenModal('datatable');
              //   }
              // );
            }}
            getJson={(json) => {
              // const selectedRecords = json.slice(0, 10);
              // console.debug(selectedRecords);
              // setState({
              //   selectedRecords,
              //   selectedRecordCount: json.length,
              // });
            }}
            style={{ minHeight: 'auto' }}
          />
        </Collapse>
      </div>
      <div className="modal-footer">
        <Button color="light" onClick={onClose}>
          {'Cancel'}
        </Button>
        <Button
          onClick={onSave}
          variant="contained"
          color="primary"
          disabled={importing}
        >
          {importing && (
            <Spinner color="dark" style={{ marginRight: 8 }} size="sm" />
          )}
          {importing ? 'Importing' : 'Import'}
        </Button>
      </div>
    </Modal>
  );

  const renderImporterDatatableModal = () => {
    if (selectedRecords && selectedRecordCount) {
      console.log(
        `Showing the first ${selectedRecords.length < 10 ? selectedRecords.length : 10} out of ${selectedRecordCount} records`
      );
    }
    return (
      <Modal
        isOpen={showImporterDatatable}
        toggle={() => setShowImporterDatatable(false)}
        onClosed={() => setShowImporterDatatable(false)}
      >
        <div className="modal-header">
          <h5 className="modal-title h2">Confirm</h5>
        </div>
        <div className="modal-body">
          {selectedRecords && selectedRecordCount && (
            <div>
              <div style={{ marginTop: 26 }}>
                {`Showing the first ${selectedRecords.length < 10 ? selectedRecords.length : 10} out of ${selectedRecordCount} records`}
              </div>
              {/* TODO We should validate here to ensure required fields are present with the correct names */}
              <DataTable
                noHeader
                pagination={false}
                columns={Object.keys(selectedRecords[0]).map((name) => ({
                  name: name
                    .replace('_', ' ')
                    .replace('_', ' ')
                    .split(' ')
                    .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
                    .join(' '),
                  selector: name,
                }))}
                data={selectedRecords}
                selectableRows={false}
              />
            </div>
          )}
        </div>
        <div className="modal-footer">
          <Button onClick={() => setShowImporterDatatable(false)}>
            Cancel
          </Button>
          <Button
            disabled={uploading}
            onClick={onImport}
            variant="contained"
            color="primary"
          >
            {uploading && (
              <Spinner
                type="border"
                color="dark"
                size="sm"
                style={{ marginRight: 10 }}
              />
            )}
            {uploading ? 'Importing...' : 'Import'}
          </Button>
        </div>
      </Modal>
    );
  };

  return (
    <>
      {renderImportModal()}
      {renderImporterDatatableModal()}
    </>
  );
};

export default CigarImporter;
